/**
 * recommendCards · v2 init recommend_cards → RecommendCardVM 适配 + 分组轮换纯函数
 *
 * 服务端已按「单/单/单/多」预拼好完整序列（接口文档 §三「服务端规则」），前端职责只有：
 *  1. 边界校验 + snake_case → 渲染 VM 的一次性转换（adaptRecommendCards）
 *  2. 按 4 张一组切片，「换一批」向后翻组、翻完循环回第 1 组（pickRecommendCardGroup）
 * 组卡、维度优先级、维值过滤都在服务端，前端不做、不判断顺序。
 *
 * 关联：
 *  · 数据源：services/aiChatInitV2.ts → recommend_cards
 *  · 编排：  services/conversationRestore.ts → loadRecommendCards
 *  · 渲染：  components/RecommendCards（EmptyState / ChatFeed 共用，经 useRecommendCards 取组）
 */

import type {
  RawRecommendCard,
  RawRecommendCardLevel
} from '../services/aiChatInitV2'
import type { RecommendCardVM } from '../components/RecommendCards/types'

/** 每批展示的卡片数 · 服务端按「单/单/单/多」4 张一组预拼，也作「换一批」滑动窗口的步长 */
export const RECOMMEND_CARD_GROUP_SIZE = 4

/** 最大公约数 · 求滑动窗口回到起点前的不同批数用 */
function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b)
}

/** 边界守卫 · 只保留字符串维值，容忍后端字段缺失 / 混入脏数据 */
function sanitizeValues(values: unknown): string[] {
  if (!Array.isArray(values)) return []
  return values.filter((v): v is string => typeof v === 'string' && v !== '')
}

/** 多级卡单层是否可渲染 · level1 必须至少有 1 个维值（UI 取第 1 个） */
function isRenderableLevel(level: unknown): level is RawRecommendCardLevel {
  return (
    typeof level === 'object' &&
    level !== null &&
    sanitizeValues((level as RawRecommendCardLevel).values).length > 0
  )
}

/**
 * 后端 recommend_cards → 渲染 VM 的一次性适配（系统边界校验）
 *
 * 逐张校验：text 缺失 / type 未知 / 多级卡缺 level1 维值 → 整张丢弃，
 * 不阻塞其余卡片渲染（后端新增 type 时前端静默跳过，补映射即可展示）。
 *
 * @param rawCards - v2 init 响应里的 recommend_cards（未校验的原始数据）
 * @returns 可直接喂给 RecommendCards 组件的 VM 列表
 *
 * @example
 * ```ts
 * adaptRecommendCards([{ type: 'single', text: '按拍摄人分类', icon_type: 'member', values: ['张三'] }])
 * // → [{ text: '按拍摄人分类', iconType: 'member', title: '按拍摄人分类', values: ['张三'] }]
 * ```
 */
export function adaptRecommendCards(rawCards: unknown): RecommendCardVM[] {
  if (!Array.isArray(rawCards)) return []
  return (rawCards as RawRecommendCard[]).flatMap((card): RecommendCardVM[] => {
    if (typeof card?.text !== 'string' || card.text === '') return []

    if (card.type === 'single') {
      return [
        {
          text: card.text,
          iconType: typeof card.icon_type === 'string' ? card.icon_type : '',
          title: card.text,
          values: sanitizeValues(card.values)
        }
      ]
    }

    if (card.type === 'multi') {
      if (!isRenderableLevel(card.level1)) return []
      return [
        {
          text: card.text,
          // 多级卡后端不带 icon_type，取一级维度的语义类型做 icon 映射
          iconType:
            typeof card.level1.dimension === 'string'
              ? card.level1.dimension
              : '',
          title: card.text,
          multi: {
            level1: sanitizeValues(card.level1.values)[0],
            level2: isRenderableLevel(card.level2)
              ? sanitizeValues(card.level2.values)
              : []
          }
        }
      ]
    }

    return []
  })
}

/**
 * 兜底推荐卡 · v2 init 失败 / 返回空序列时使用，保证缺省页永远有可点入口
 *
 * 与 v1 的 FALLBACK_RECOMMENDED_PROMPTS 同一取向（按拍摄月份 / 按拍摄人），
 * 不带维值标签（兜底场景拿不到团队真实维值）。
 */
export const FALLBACK_RECOMMEND_CARDS: RecommendCardVM[] = [
  {
    text: '按拍摄月份分类',
    iconType: 'time',
    title: '按拍摄月份分类'
  },
  {
    text: '按拍摄人分类',
    iconType: 'member',
    title: '按拍摄人分类'
  }
]

/**
 * 是否可「换一批」· 仅当卡片数超过一批（4 张）才有下一批可翻
 *
 * ≤4 张（含 v2 init 失败的 2 张兜底）时没有第二批，调用方据此隐藏「换一批」按钮。
 *
 * @param cards - 适配后的完整卡片序列
 * @returns 卡片数 > 4 时为 true
 */
export function canRotateRecommendCards(cards: RecommendCardVM[]): boolean {
  return cards.length > RECOMMEND_CARD_GROUP_SIZE
}

/**
 * 「换一批」轮换的不同批数（环长度）· 每批固定 4 张、步长 4 的滑动窗口，
 * 回到起点前的不同批数 = N / gcd(N, 4)（N 为总数）。
 *
 * @param cards - 适配后的完整卡片序列
 * @returns ≤4 张返回 1（不轮换）；否则 N / gcd(N, 4)。例：6 张→3、28 张→7、8 张→2
 */
export function recommendCardCycleCount(cards: RecommendCardVM[]): number {
  const n = cards.length
  if (n <= RECOMMEND_CARD_GROUP_SIZE) return 1
  return n / gcd(n, RECOMMEND_CARD_GROUP_SIZE)
}

/**
 * 取第 batchIndex 批推荐卡（固定 4 张的滑动窗口，步长 4，越界回卷到开头）
 *
 * ≤4 张：直接全展示，不轮换。>4 张：从 (batchIndex*4) % N 起连取 4 张，
 * 超尾部回卷 —— 例 6 张：前 4 张 → 后 2 张+前 2 张 → 中间 2 张+后 2 张，循环。
 * 恰好 4 的倍数（如 28）时等价于原来的非重叠分组，行为不变。
 * 空序列返回 FALLBACK_RECOMMEND_CARDS 兜底。
 *
 * @param cards - 适配后的完整卡片序列
 * @param batchIndex - 批下标（任意非负整数，内部取模回卷）
 * @returns 当前批的 4 张卡片（≤4 张时返回全部）
 *
 * @example
 * ```ts
 * pickRecommendCardGroup(sixCards, 0)  // [0,1,2,3]
 * pickRecommendCardGroup(sixCards, 1)  // [4,5,0,1]（后2+前2）
 * pickRecommendCardGroup(fourCards, 3) // 四张全展示（不轮换）
 * pickRecommendCardGroup([], 0)        // FALLBACK_RECOMMEND_CARDS
 * ```
 */
export function pickRecommendCardGroup(
  cards: RecommendCardVM[],
  batchIndex: number
): RecommendCardVM[] {
  if (cards.length === 0) return FALLBACK_RECOMMEND_CARDS
  const size = RECOMMEND_CARD_GROUP_SIZE
  const n = cards.length
  if (n <= size) return cards
  const start = (batchIndex * size) % n
  const batch: RecommendCardVM[] = []
  for (let i = 0; i < size; i++) {
    batch.push(cards[(start + i) % n])
  }
  return batch
}
